import React, { useState, useEffect } from "react";
import tw from "tailwind-styled-components";
import { MdClose } from "react-icons/md";
import { CircularProgress } from "@mui/material";
import { ImpactText, ModalHeader } from "../Styles/InputStyles";

const GlobalSettingsModal = ({
    onClose,
    settingsData,
    editSettingsMutate,
    isLoading,
    fetchSettingsLoading,
    isDataLoaded,
    editSettingsLoading
}) => {
    const [settings, setSettings] = useState({
        manualMoveIn: false,
        manualMoveOut: false,
        autoAssignComplaint: false,
        bookingAmt: false,
    });

    useEffect(() => {
        if (settingsData) {
            setSettings({
                manualMoveIn: settingsData?.manualMoveIn === 1,
                manualMoveOut: settingsData?.manualMoveOut === 1,
                autoAssignComplaint: settingsData?.autoAssignComplaint === 1,
                bookingAmt: settingsData?.bookingAmt === 1,
            });
        }
    }, [settingsData]);

    const toggleSetting = (key) => {
        const newValue = !settings[key];
        const updatedSettings = { ...settings, [key]: newValue };
        setSettings(updatedSettings);

        const payload = {
            manualMoveIn: updatedSettings.manualMoveIn ? 1 : 0,
            manualMoveOut: updatedSettings.manualMoveOut ? 1 : 0,
            autoAssignComplaint: updatedSettings.autoAssignComplaint ? 1 : 0,
            bookingAmt: updatedSettings.bookingAmt ? 1 : 0,
        };
        editSettingsMutate(payload);
    };

    const showLoading = fetchSettingsLoading || !isDataLoaded;

    return (
        <div className="bg-white w-full h-full flex flex-col">
            <ModalHeader>
                <HeaderTitle>Operational Controls</HeaderTitle>
                <CloseButton onClick={onClose}>
                    <MdClose color="white" size={24} />
                </CloseButton>
            </ModalHeader>

            {showLoading ? (
                <div className="flex items-center justify-center h-full">
                    <CircularProgress />
                </div>
            ) : (
                <>
                    <ContentContainer>
                        <ContentWrapper>
                            <Section>
                                {/* <div className="mb-2">
                                    <p className="text-xs text-gray-500 uppercase tracking-wide">
                                        Payments, Expenses & Dues
                                    </p>
                                </div> */}

                                <SettingsGrid>

                                    {/* Manual Move In */}
                                    <SettingCard $variant="info" $active={settings.manualMoveIn}>
                                        <SettingContent>
                                            <SettingHeader>
                                                <TitleRow>
                                                    <SettingTitle>Manual Move In</SettingTitle>
                                                    <BadgeGroup>
                                                        {settings.manualMoveIn && (
                                                            <StatusBadge $variant="info">Active</StatusBadge>
                                                        )}
                                                    </BadgeGroup>
                                                </TitleRow>
                                                <SettingDescription>
                                                    If enabled, staff must manually complete the tenant Move-in process after the tenant joins the property.
                                                </SettingDescription>
                                                <ImpactText className="text-red-500">
                                                    Tenants Move-Ins will no longer be completed automatically and must be marked maually by staff.
                                                </ImpactText>

                                            </SettingHeader>
                                            <ToggleWrapper>
                                                <Toggle
                                                    checked={settings.manualMoveIn}
                                                    onChange={() => toggleSetting("manualMoveIn")}
                                                    disabled={isLoading || editSettingsLoading}
                                                />
                                            </ToggleWrapper>
                                        </SettingContent>
                                    </SettingCard>

                                    {/* Manual Move Out */}
                                    <SettingCard $variant="info" $active={settings.manualMoveOut}>
                                        <SettingContent>
                                            <SettingHeader>
                                                <TitleRow>
                                                    <SettingTitle>Manual Move Out</SettingTitle>
                                                    <BadgeGroup>
                                                        {settings.manualMoveOut && (
                                                            <StatusBadge $variant="info">Active</StatusBadge>
                                                        )}
                                                    </BadgeGroup>
                                                </TitleRow>
                                                <SettingDescription>
                                                    If enabled, staff must manually complete the tenant Move-out process to mark the bed as vacant after the tenant leaves the property.
                                                </SettingDescription>
                                                <ImpactText className="text-red-500">
                                                    Beds will not marked vacant automatically by the Kipinn platform after tenant move-out.
                                                </ImpactText>

                                            </SettingHeader>
                                            <ToggleWrapper>
                                                <Toggle
                                                    checked={settings.manualMoveOut}
                                                    onChange={() => toggleSetting("manualMoveOut")}
                                                    disabled={isLoading || editSettingsLoading}
                                                />
                                            </ToggleWrapper>
                                        </SettingContent>
                                    </SettingCard>

                                    {/* Auto Assign Complaint */}
                                    <SettingCard $variant="success" $active={settings.autoAssignComplaint}>
                                        <SettingContent>
                                            <SettingHeader>
                                                <TitleRow>
                                                    <SettingTitle>Auto Assign Complaints</SettingTitle>
                                                    <BadgeGroup>
                                                        {settings.autoAssignComplaint && (
                                                            <StatusBadge $variant="success">Active</StatusBadge>
                                                        )}
                                                    </BadgeGroup>
                                                </TitleRow>
                                                <SettingDescription>
                                                    Automatically assign new complaints to staff with matching roles. If no specific role is assigned, tickets are redirected to the general helpdesk.
                                                </SettingDescription>

                                            </SettingHeader>
                                            <ToggleWrapper>
                                                <Toggle
                                                    checked={settings.autoAssignComplaint}
                                                    onChange={() => toggleSetting("autoAssignComplaint")}
                                                    disabled={isLoading || editSettingsLoading}
                                                />
                                            </ToggleWrapper>
                                        </SettingContent>
                                    </SettingCard>

                                    {/* Booking Amount */}
                                    <SettingCard $variant="info" $active={settings.bookingAmt}>
                                        <SettingContent>
                                            <SettingHeader>
                                                <TitleRow>
                                                    <SettingTitle>Booking Amount Collection</SettingTitle>
                                                    <BadgeGroup>
                                                        {settings.bookingAmt && (
                                                            <StatusBadge $variant="info">Active</StatusBadge>
                                                        )}
                                                    </BadgeGroup>
                                                </TitleRow>
                                                <SettingDescription>
                                                    If enabled, you can collect the booking amount along with rent and security while adding a tenant
                                                </SettingDescription>

                                            </SettingHeader>
                                            <ToggleWrapper>
                                                <Toggle
                                                    checked={settings.bookingAmt}
                                                    onChange={() => toggleSetting("bookingAmt")}
                                                    disabled={isLoading || editSettingsLoading}
                                                />
                                            </ToggleWrapper>
                                        </SettingContent>
                                    </SettingCard>
                                </SettingsGrid>
                            </Section>
                        </ContentWrapper>
                    </ContentContainer>
                </>
            )}
        </div>
    );
};

// Styled Components
const ContentContainer = tw.div`flex-1 overflow-y-auto`;
const ContentWrapper = tw.div`p-6`;
const Section = tw.div`space-y-6`;
const HeaderTitle = tw.h1`text-white text-lg font-medium`;
const CloseButton = tw.button``;
const SettingsGrid = tw.div`space-y-4`;

const Toggle = ({ checked, onChange, disabled }) => (
    <ToggleLabel disabled={disabled}>
        <ToggleInput
            type="checkbox"
            checked={checked}
            onChange={onChange}
            disabled={disabled}
        />
        <ToggleSlider checked={checked} disabled={disabled}>
            <ToggleThumb checked={checked} />
        </ToggleSlider>
    </ToggleLabel>
);

const ToggleLabel = tw.label`${props => props.disabled ? 'cursor-not-allowed' : 'cursor-pointer'} relative inline-flex items-center`;
const ToggleInput = tw.input`sr-only peer`;
const ToggleSlider = tw.div`relative w-12 h-6 rounded-full transition-colors duration-200 ease-in-out ${props => props.checked ? 'bg-primary-color' : 'bg-gray-300'}`;
// const ToggleSlider = tw.div`relative w-12 h-6 rounded-full transition-colors duration-200 ease-in-out ${props => props.disabled ? 'bg-gray-200 cursor-not-allowed' : props.checked ? 'bg-primary-color' : 'bg-gray-300'}`;
const ToggleThumb = tw.div`absolute top-0.5 left-0.5 w-5 h-5 bg-white border border-gray-300 rounded-full shadow-sm transition-transform duration-200 ease-in-out ${props => props.checked ? 'transform translate-x-6' : ''}`;

const SettingCard = tw.div`
  relative rounded-xl border p-4 transition-all duration-200 bg-white
  ${(p) =>
        p.$active
            ? "border-gray-300 shadow-sm"
            : "border-gray-200 hover:border-gray-300 hover:shadow-sm"}
`;

const SettingContent = tw.div`
  flex items-center justify-between gap-4
`;

const SettingHeader = tw.div`flex-1`;

const SettingTitle = tw.h3`
  text-sm font-semibold text-gray-800
`;

const SettingDescription = tw.p`
  text-xs text-gray-500 leading-relaxed
`;

const ToggleWrapper = tw.div`
  flex items-center justify-center
`;

const StatusBadge = tw.span`
  text-[10px] font-medium px-2 py-0.5 rounded-full
  ${(p) =>
        p.$variant === "warning"
            ? "bg-yellow-100 text-yellow-700"
            : p.$variant === "success"
                ? "bg-green-100 text-green-700"
                : "bg-blue-100 text-blue-700"}
`;

const HelperText = tw.p`
  text-xs text-gray-400 mt-1
`;

const TitleRow = tw.div`
  flex items-center justify-between gap-2 mb-1
`;

const BadgeGroup = tw.div`
  flex items-center gap-1.5 flex-wrap
`;

export default GlobalSettingsModal;